import React from 'react'
import _ from 'lodash'
import { renderToString } from 'react-dom/server'
import { runQuery } from '../../fetchUtils'
import { ExpeditionProgress } from './ExpeditionProgress'
import { Loading, ToggleDropDown } from '../../components/Shared'
import {
	faCircleCheck,
	faCircleExclamation,
	faCircleXmark
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import '../../assets/css/pages/track_progress/expedition-overview.css'

const VIEW = 'view'
const DOWNLOAD = 'download'

type Props = {
	expeditionAssignments: [
		{
			class_id: number
			class_name: string
			expedition_description: string
			expedition_id: string
			expedition_instances: [any]
			expedition_name: string
			expedition_version: number
		}
	]
}

export const ExpeditionProgressRow: React.FC = ({ expeditionAssignments }: Props) => {
	const [open, setOpen] = React.useState<Set<string>>(new Set())

	const toggleOpen = (key: string) => {
		setOpen(prev => {
			prev.has(key) ? prev.delete(key) : prev.add(key)
			return new Set([...prev])
		})
	}

	const ProgressStatus = [
		{
			className: '--not-started',
			status: 'Not Started'
		},
		{
			className: '--in-progress',
			status: 'In Progress'
		},
		{
			className: '--failed',
			status: 'Completed without success'
		},
		{ className: '--success', status: 'Completed with success' }
	]

	const handleCompletionStatus = (
		playerProgress,
		simResult
	): 'success' | 'failed' | 'in-progress' | 'not-started' => {
		if (_.isUndefined(playerProgress) && _.isUndefined(simResult)) return 'not-started'
		else if (simResult) {
			return simResult.success ? 'success' : 'failed'
		} else if (playerProgress) return 'in-progress'
	}

	return (
		<>
			<ul className='StatusLegend'>
				<li className='StatusLegend__head'>Status:</li>
				{ProgressStatus.map((progress, progressInd) => (
					<li key={progress.className + progressInd} className='StatusLegend__legend'>
						<div className={`${progress.className} __icon`}></div>
						<span>=</span>
						{progress.status}
					</li>
				))}
			</ul>
			<ul className='ExpeditionOverview'>
				{expeditionAssignments.map((sectionAssignment, sectionAssignmentInd) => {
					const {
						class_name: className,
						class_id: sectionId,
						expedition_name: expeditionName,
						expedition_instances: groupAssignments,
						expedition_id: expeditionId,
						expedition_version: expeditionVersion
					} = sectionAssignment

					const uniqKey = `${sectionId}-${expeditionId}-${expeditionVersion}-${sectionAssignmentInd}`
					const assignedDate = !_.isUndefined(groupAssignments)
						? `(${groupAssignments[0].assigned_at.split('T')[0]})`
						: ''

					return (
						<li
							key={`Expeditions-${sectionId}-${sectionAssignmentInd}`}
							className='ExpeditionOverview__byClass'>
							<div className='ExpeditionOverview__byClass__head'>
								<span>{className}</span>
							</div>

							<div className='ExpeditionOverview__byExpedition'>
								<div className='ExpeditionOverview__byExpedition__head'>
									<span>
										{assignedDate} {expeditionName}
									</span>
									<ToggleDropDown
										open={open.has(uniqKey)}
										setOpen={() => toggleOpen(uniqKey)}
									/>
								</div>
								{open.has(uniqKey) && (
									<ul
										key={`Expeditions-${sectionId}-assignments`}
										className='ClassOverview'>
										{groupAssignments.map((expedition, expeditionInd) => {
											const {
												expedition_instance_id: expeditionInstanceId,
												simulation_result: result,
												player_progress,
												students
											}: {
												expedition_instance_id: string
												player_progress: boolean
												simulation_result: { success: boolean }
												students: [{ id: string; full_name: string }]
											} = expedition
											return (
												<li
													key={`${expeditionInstanceId}`}
													className='ClassOverview__byAssignmentGroup'>
													<ByAssignmentGroups
														ind={expeditionInd}
														students={students}
														className={className}
														expeditionName={expeditionName}
														id={expeditionInstanceId}
														result={{
															status: handleCompletionStatus(
																player_progress,
																result
															)
														}}
														uniqKey={`${expeditionId}-${expeditionInstanceId}`}
													/>
												</li>
											)
										})}
									</ul>
								)}
							</div>
						</li>
					)
				})}
			</ul>
		</>
	)
}

type ByAssignmentGroupsProp = {
	ind: number
	students: [any]
	className: string
	expeditionName: string
	result: { status: string }
	id: string
	uniqKey: string
}

const ByAssignmentGroups: React.FC<ByAssignmentGroupsProp> = ({
	ind,
	students,
	className,
	expeditionName,
	result,
	id,
	uniqKey
}) => {
	const resetLoad = { state: false, action: '', key: '' }
	const [isLoading, setIsLoading] = React.useState(resetLoad)

	const generateReport = (groupDetail: {}, instanceDetail: {}, action: string) => {
		const html = renderToString(
			<ExpeditionProgress groupInfo={groupDetail} playtestData={instanceDetail} />
		)
		const blob = new Blob([html], { type: 'text/html' })
		const url = URL.createObjectURL(blob)
		const tempEl = document.createElement('a')
		tempEl.href = url
		tempEl.target = '_blank'
		if (action === DOWNLOAD) {
			tempEl.download = 'Expedition_progress.html'
		}
		document.body.appendChild(tempEl)
		tempEl.click()
		setIsLoading(resetLoad)

		setTimeout(() => {
			URL.revokeObjectURL(url)
			tempEl.parentNode.removeChild(tempEl)
		}, 2000)
	}

	const fetchInstanceDetail = (instanceId: string, callbacks: {}) => {
		console.log(`getting expedition progress detail for ${instanceId}`)
		const fetchInstanceDetail = `
			query LoadExpeditionInstanceDetail($instanceId:ID!){
				expeditionInstanceDetail(instanceId:$instanceId)
			}
		`

		const vars = {
			instanceId: instanceId
		}

		runQuery(fetchInstanceDetail, vars, callbacks)
	}

	const handleGenerateReport = (instanceId: string, groupDetail: {}, action: string) => {
		setIsLoading({ state: true, action, key: instanceId })
		const callbacks = {
			onComplete: json => {
				const result = json.data.expeditionInstanceDetail
				if (result[0]?.errors) {
					console.error(result[0].errors)
					setIsLoading(resetLoad)
				} else {
					generateReport(groupDetail, result.Item, action)
				}
			},
			onFailure: error => {
				console.error(error)
				setIsLoading(resetLoad)
			}
		}

		fetchInstanceDetail(instanceId, callbacks)
	}

	const groupDetail = {
		className,
		expeditionName,
		students
	}

	return (
		<ul className='AssignmentOverview'>
			<li key={uniqKey + id} className='AssignmentOverview__byGroup'>
				<div className='AssignmentOverview__byGroup__head'>
					<div className={`Status __icon --${result.status}`}></div>
					<span className='__groupName'>Group {ind + 1}: </span>
					{students.map(student => student.full_name).join(',')}
				</div>

				<div className='AssignmentOverview__byGroup__actions'>
					<button
						className='Button--default'
						type='button'
						onClick={() => {
							handleGenerateReport(id, groupDetail, VIEW)
						}}>
						{isLoading.state && isLoading.action === VIEW && isLoading.key === id && (
							<Loading size={14} />
						)}
						<span>View Details</span>
					</button>
					<button
						className='Button--default--invert'
						type='button'
						onClick={() => {
							console.log(`downloading expedition progress report for ${id}`)
							handleGenerateReport(id, groupDetail, DOWNLOAD)
						}}>
						{isLoading.state &&
							isLoading.action === DOWNLOAD &&
							isLoading.key === id && <Loading size={14} />}
						Download
					</button>
				</div>
			</li>
		</ul>
	)
}
